import ContentBody from '@op/components/ContentBody';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Layout, Segment, Tabs } from 'tea-component';
import { connect, getLocale, useIntl, useParams } from 'umi';
import GptReport from '../gptReport/preview';
import { debounce } from 'throttle-debounce';
import '../gptReport/styles/markdown-preview.scss';
import '../gptReport/styles/index.scss';
import './index.scss';
import Gpt from '../gpt/preview';
import GPTPointFunc from '@op/operationLog/pages/gpt';
import PresetPointFunc from '@op/operationLog/pages/preset';
import CardCommon from '@op/components/CardCommon';
import IFrameCard from '@op/components/IFrameCard';
import classnames from 'classnames';
import Loading from '../report/components/loading';
import useReportMenuTab from '../hooks/useReportMenuTab';
import {
  buriedBtnPoint,
  buriedPagePoint,
  useInitBuriedPointInfo,
} from '@op/operationLog/buriedPoint';
import { useUpdateEffect } from 'ahooks';
import MultipleDropSelect from '../insight/components/multiple-drop-select';
import moment from 'moment';
import usePageName from '@op/hooks/usePageName';

/**
 * 可以针对bussiness type拓展 针对各种类型报表
 * @param param0
 * @returns
 */
const CustomizeReport = ({ gameInfo, wholeGameInfo, listMenu }: any) => {
  const { Content, Body } = Layout;
  const { id, name } = useParams<{ id: string; name: string }>();
  const { pageName, sourceType } = usePageName({ name, listMenu });
  const { formatMessage } = useIntl();

  const contentRef = useRef<HTMLElement>(null);
  const [scrolling, setScrolling] = useState(false);
  const [segment, setSegment] = useState('');
  const [segmentOptions, setSegmentOptions] = useState<
    Array<{ text: string; value: string; url: string; pointText: string }>
  >([]);
  const [currentPDF, setCurrentPDF] = useState({ url: '', pageName: '' });

  useEffect(() => {
    // 设置初始状态
    setScrolling(false);
    setSegment('');
    setSegmentOptions([]);
    setCurrentPDF({ url: '', pageName: '' });
  }, [id, name]);

  useInitBuriedPointInfo({
    buriedPoint:
      name === 'Spotlight Reports'
        ? PresetPointFunc(location.pathname)
        : GPTPointFunc(location.pathname),
    triggerPagePoint: false,
  });

  const { tabs, loading, activeId, setActiveId } = useReportMenuTab({
    gameInfo,
    menuId: id,
    name,
    sourceType,
  });

  useUpdateEffect(() => {
    const item: any = tabs?.find((val) => activeId === val?.id) || {};
    if (item?.name_en) {
      buriedPagePoint(undefined, item?.name_en);
    }
  }, [activeId]);

  const currentTab: React.ReactNode = useMemo(() => {
    const { ext, business_type } =
      tabs?.find((val) => activeId === val?.id) || {};

    const GPTElementsMap: { [key: string]: Function } = {
      gpt_by_language: () => {
        const { type, kind } = ext || { type: 1, kind: 3 };
        return (
          <div key={'gpt_by_language'}>
            <GptReport
              wholeGameInfo={wholeGameInfo}
              gameInfo={gameInfo}
              scrolling={scrolling}
              contentRef={contentRef}
              reportId={kind}
              reportType={Number(type)}
            ></GptReport>
          </div>
        );
      },
      gpt_by_kol: () => {
        const { kind, type } = ext || { type: 1, kind: 4 };
        return (
          <div key={'gpt_by_kol'}>
            <GptReport
              wholeGameInfo={wholeGameInfo}
              gameInfo={gameInfo}
              scrolling={scrolling}
              contentRef={contentRef}
              reportId={kind}
              reportType={Number(type)}
            ></GptReport>
          </div>
        );
      },
      gpt_auto_report: () => {
        const { kind } = ext || { type: 1, kind: 1 };
        return (
          <div key={'gpt_auto_report'}>
            <Gpt
              custom={true} // 代表传统的GPT报告
              wholeGameInfo={wholeGameInfo}
              gameInfo={gameInfo}
              scrolling={scrolling}
              contentRef={contentRef}
              key={activeId}
              kind={kind}
              needAddPoint={false}
              handleKindOnchange={(value) => {
                console.log('handleKindOnchange ', value);
              }}
            ></Gpt>
          </div>
        );
      },
    };

    const GptElements = GPTElementsMap[business_type as unknown as string];
    return GptElements?.() || null;
  }, [tabs, activeId, wholeGameInfo, gameInfo, scrolling]);

  useEffect(() => {
    const isEn = getLocale() === 'en-US';
    const otherInfo: any = tabs.find((val) => activeId === val?.id) || {};
    const children = otherInfo?.children || [];
    const extParams = otherInfo?.ext || {};
    if (children.length === 0 && Object.keys(extParams).length !== 0) {
      if (extParams?.report_url) {
        setCurrentPDF({
          url: extParams?.report_url,
          pageName: isEn ? otherInfo.name_en : otherInfo?.name_cn,
        });
        return;
      }
    }

    const segmentOpt =
      children?.map((val: any, index: number) => ({
        text: isEn ? val.name_en : val?.name_cn,
        value: val.id.toString() + index,
        url: val.url,
        pointText: val.name_en,
        date: moment(val?.date || moment().add(index, 'second')).format(
          'YYYY-MM-DD HH:mm:ss',
        ), /// 兜底无时间数据
        title: val.name_cn,
        title_en: val.name_en,
      })) || [];
    if (segmentOpt.length > 0) {
      setSegment(segmentOpt[0].value);
    }
    setSegmentOptions(segmentOpt);
  }, [activeId, formatMessage, tabs]);

  const selectedSegment = useMemo(
    () => segmentOptions?.filter((val) => val.value === segment)[0] || {},
    [segmentOptions, segment],
  );

  return (
    <Layout>
      <Body className={'markdown-preview-wrapper'}>
        <Content
          className={classnames('dbo-gpt', 'dbo-markdown ', 'markdown-preview')}
          ref={contentRef}
          onScrollCapture={debounce(100, () => {
            setScrolling(true);
            setTimeout(() => {
              setScrolling(false);
            }, 100);
          })}
        >
          <Loading loading={loading}>
            <ContentBody
              cardLoadingStyle={{height: "100%"}}
              pageName={decodeURIComponent(name) as any}
              className={classnames(
                currentTab ?? 'dbo-customize-report',
                'dbo-markdown-preview',
                'dbo-markdown-preview-custom',
              )}
              contentHeard={
                <Content.Header
                  title={pageName}
                  className={tabs?.length > 1 ? 'contentHead-normal' : ''}
                >
                  {tabs?.length > 1 && (
                    <div className="header-tab">
                      <Tabs
                        ceiling
                        tabs={tabs}
                        animated={false}
                        activeId={activeId}
                        onActive={(tab) => {
                          setActiveId(tab.id);
                        }}
                      />
                    </div>
                  )}
                </Content.Header>
              }
            >
              <>
                {currentTab ?? (
                  <CardCommon
                    contentCard
                    empty={segmentOptions.length === 0 && !currentPDF?.url}
                    className="dbo-customize-report"
                  >
                    {segmentOptions.length > 8 ? (
                      <MultipleDropSelect
                        reportInsightConfig={segmentOptions}
                        handleReportChange={(report) => {
                          const { value, pointText } = report;
                          if (pointText) {
                            buriedBtnPoint('Report_Tab', pointText);
                          }
                          setSegment(value);
                        }}
                        handleInit={(report) => {
                          const { value, pointText } = report;
                          if (pointText) {
                            buriedBtnPoint('Report_Tab', pointText);
                          }
                          setSegment(value);
                        }}
                      ></MultipleDropSelect>
                    ) : (
                      <Segment
                        className="segment-light"
                        value={segment}
                        onChange={(value: string) => {
                          const item = segmentOptions.find(
                            (item) => item.value === value,
                          );
                          if (item?.pointText) {
                            buriedBtnPoint('Report_Tab', item.pointText);
                          }
                          setSegment(value);
                        }}
                        options={segmentOptions}
                        style={{ marginRight: 10 }}
                      />
                    )}
                    <IFrameCard
                      url={currentPDF?.url || selectedSegment?.url}
                      pageName={currentPDF?.pageName || selectedSegment?.text}
                    ></IFrameCard>
                  </CardCommon>
                )}
              </>
            </ContentBody>
          </Loading>
        </Content>
      </Body>
    </Layout>
  );
};

export default connect((state: { opinionCommon: any }) => {
  const { opinionCommon } = state;
  return {
    gameInfo: opinionCommon.gameInfo,
    wholeGameInfo: opinionCommon.wholeGameInfo,
    listMenu: opinionCommon.listMenu,
  };
})(CustomizeReport);
